import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { servicesAPI, productsAPI } from '../api';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');

const getImageUrl = (item) => {
  try {
    const images = typeof item.images === 'string' ? JSON.parse(item.images) : (item.images || []);
    if (images.length > 0) return `${API_URL}${images[0]}`;
  } catch (e) {}
  return null;
};

const ItemCard = ({ item, accentColor }) => {
  const img = getImageUrl(item);
  const title = item.title || item.product_name || '';
  const url = item._type === 'service' ? `/services/${item.id}` : `/products/${item.id}`;
  const price = Number(item.price || 0).toLocaleString();
  const isPremium = item.is_premium === 1 || item.seller_plan === 'premium' || item.seller_plan === 'basic';

  return (
    <Link to={url} className="flex gap-2.5 p-2.5 hover:bg-gray-50 transition-colors group">
      <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 ring-1 ring-gray-200/50">
        {img ? (
          <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">
            {item._type === 'service' ? '🔧' : '📦'}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-gray-800 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
          {isPremium && <span className="text-yellow-500 mr-0.5">⭐</span>}
          {title}
        </p>
        <p className={`text-[11px] font-bold mt-0.5 text-${accentColor}-600`}>
          {price} RWF
        </p>
        {item.category_name && (
          <p className="text-[9px] text-gray-400 mt-0.5 truncate">{item.category_name}</p>
        )}
      </div>
    </Link>
  );
};

const ScrollSection = ({ title, icon, items, accentColor, linkTo, linkLabel }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col" style={{ maxHeight: 'calc(50vh - 3rem)' }}>
    <div className={`px-3 py-3 border-b border-gray-100 bg-gradient-to-r from-${accentColor}-50 to-white flex-shrink-0`}>
      <div className="flex items-center justify-between">
        <h4 className="text-[12px] font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
          <span className="text-sm">{icon}</span> {title}
        </h4>
        <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full font-medium">{items.length}</span>
      </div>
    </div>
    <div className="divide-y divide-gray-50 overflow-y-auto flex-1 scrollbar-thin">
      {items.map(item => (
        <ItemCard key={`${item._type}-${item.id}`} item={item} accentColor={accentColor} />
      ))}
    </div>
    <Link to={linkTo} className={`block text-center text-[11px] font-semibold py-2.5 border-t border-gray-100 text-${accentColor}-600 hover:bg-${accentColor}-50 transition-colors flex-shrink-0`}>
      {linkLabel}
    </Link>
  </div>
);

const SidePanel = () => {
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    servicesAPI.getAll({ limit: 20, page: 1 })
      .then(res => setServices((res.data.services || []).map(s => ({ ...s, _type: 'service' }))))
      .catch(() => {});
    productsAPI.getAll({ limit: 20, page: 1 })
      .then(res => setProducts((res.data.products || []).map(p => ({ ...p, _type: 'product' }))))
      .catch(() => {});
  }, []);

  if (services.length === 0 && products.length === 0) return null;

  return (
    <div className="hidden 2xl:flex flex-col w-[190px] flex-shrink-0 sticky top-20 max-h-[calc(100vh-6rem)] space-y-3">
      {services.length > 0 && (
        <ScrollSection
          title="Trending Services"
          icon="🔥"
          items={services}
          accentColor="primary"
          linkTo="/services"
          linkLabel="View All Services →"
        />
      )}
      {products.length > 0 && (
        <ScrollSection
          title="Popular Products"
          icon="⚡"
          items={products}
          accentColor="green"
          linkTo="/products"
          linkLabel="View All Products →"
        />
      )}
    </div>
  );
};

export default SidePanel;
